/**
 * UnsavedChangesModal - 3-button confirmation dialog for unsaved changes.
 *
 * Presents the user with three options:
 * - "Save changes" — saves pending changes then proceeds with the guarded action
 * - "Close without saving" — discards changes and proceeds
 * - "Cancel" — returns to editing
 */

import { AlertTriangle } from '@untitledui/icons';
import { useId } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { Button } from '@/lib/base/buttons/button';
import { sanitizeError } from '@/lib/utils/errors';
import { cx } from '@/utils/cx';

/**
 * Props for UnsavedChangesModal. `useUnsavedChangesGuard` (`@/hooks`) returns
 * a ready-made `modalProps` object of this shape.
 */
export interface UnsavedChangesModalProps {
  isOpen: boolean;
  onSave: (() => Promise<void>) | null;
  onDiscard: () => void;
  onCancel: () => void;
  isSaving: boolean;
  saveError?: string | null;
}

/**
 * UnsavedChangesModal component.
 *
 * @example
 * ```tsx
 * const { modalProps } = useUnsavedChangesGuard({ ... });
 * return <UnsavedChangesModal {...modalProps} />;
 * ```
 */
export function UnsavedChangesModal({
  isOpen,
  onSave,
  onDiscard,
  onCancel,
  isSaving,
  saveError,
}: UnsavedChangesModalProps) {
  const zIndex = useStackingZIndex();
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const descriptionId = useId();

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open && !isSaving) {
          onCancel();
        }
      }}
      isDismissable={!isSaving}
      className={cx(
        'fixed inset-0 flex items-center justify-center bg-black/50',
        !prefersReducedMotion && 'animate-in fade-in duration-200'
      )}
      style={{ zIndex }}
    >
      <Modal
        className={cx(
          'relative max-w-lg w-full mx-4 bg-bg-primary rounded-xl shadow-2xl',
          !prefersReducedMotion &&
            'animate-in zoom-in-95 slide-in-from-bottom-4 duration-200'
        )}
        style={{ zIndex: zIndex + 10 }}
      >
        <Dialog
          data-component='modal'
          data-instance='unsaved-changes'
          aria-describedby={descriptionId}
          className='outline-none'
        >
          {() => (
            <StackingLayer>
              <div className='p-6'>
                {/* Icon and Title */}
                <div className='flex items-start gap-4 mb-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-warning-50 flex items-center justify-center'>
                    <AlertTriangle className='w-6 h-6 text-warning-600' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <Heading
                      slot='title'
                      className='text-lg font-semibold text-fg-primary mb-1'
                    >
                      Unsaved changes
                    </Heading>
                    <p id={descriptionId} className='text-sm text-fg-secondary'>
                      You have unsaved changes. What would you like to do?
                    </p>
                    {saveError && (
                      <p
                        className='text-sm text-error-600 mt-2'
                        aria-live='polite'
                      >
                        Save failed: {sanitizeError(saveError)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className='flex items-center justify-end gap-3'>
                  <Button
                    color='secondary'
                    size='md'
                    onClick={onCancel}
                    isDisabled={isSaving}
                    data-testid='unsaved-changes-cancel'
                  >
                    Cancel
                  </Button>
                  <Button
                    color='secondary'
                    size='md'
                    onClick={onDiscard}
                    isDisabled={isSaving}
                    data-testid='unsaved-changes-discard'
                  >
                    Continue without saving
                  </Button>
                  {onSave && (
                    <Button
                      color='primary'
                      size='md'
                      onClick={onSave}
                      isDisabled={isSaving}
                      isLoading={isSaving}
                      autoFocus
                      data-testid='unsaved-changes-save'
                    >
                      Save changes
                    </Button>
                  )}
                </div>
              </div>
            </StackingLayer>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
